import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle, FilePdf, FloppyDisk, LockKey, ArrowLeft as ArrowLeftIcon,
  TreeStructure, Warning,
} from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatApiError, scoreBandClass, scoreBandLabel, formatDate } from "../lib/utils-extra";

/* ---------- scoring helpers (mirror backend scoring.py) ---------- */
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const ratingPct = (r, scale) => { const v = num(r); return v === null ? null : Math.max(0, Math.min(100, v / (scale === "0-5" ? 5 : 10) * 100)); };
const objPct = (measurement, target, actual) => {
  const t = num(target), a = num(actual);
  if (measurement === "completion") return a === null ? null : (a >= 1 ? 100 : 0);
  if (measurement === "lower_is_better") { if (t === null || a === null) return null; if (a <= t) return 100; return Math.max(0, 100 - ((a - t) / Math.max(t, 1)) * 100); }
  if (t === null || t === 0 || a === null) { if (t === 0 && a !== null) return a >= 0 ? 100 : 0; return null; }
  return Math.max(0, Math.min(100, a / t * 100));
};
const rollup = (items, get) => { let tw = 0, acc = 0; items.forEach((it) => { const s = get(it); const w = Number(it.weight || 0); if (s !== null && w > 0) { acc += s * w; tw += w; } }); return tw > 0 ? acc / tw : null; };
const kraType = (k) => (k.primary_source === "objective" ? ((k.enable_self_rating || k.enable_supervisor_rating) ? "Hybrid" : "Objective") : "Behavioral");

function computeKra(k) {
  const obj = k.enable_objective ? objPct(k.measurement || "higher_is_better", k.objective_target, k.objective_actual) : null;
  const selfPct = k.enable_self_rating ? ratingPct(k.self_rating, k.rating_scale) : null;
  const supPct = k.enable_supervisor_rating ? ratingPct(k.supervisor_rating, k.rating_scale) : null;
  const score = k.primary_source === "objective" ? obj : supPct;
  const sup = num(k.supervisor_rating), slf = num(k.self_rating);
  const variance = (sup !== null && slf !== null) ? Math.abs(sup - slf) : null;
  const direction = (sup !== null && slf !== null) ? (slf > sup ? "employee_higher" : (sup > slf ? "manager_higher" : "aligned")) : null;
  const complete = k.primary_source === "objective" ? obj !== null : supPct !== null;
  return { score, selfPct, objScore: obj, variance, direction, complete };
}
function computeLine(l) {
  if (l.has_subcategories && l.subcategories?.length) {
    const subs = l.subcategories.map((s) => {
      const kras = s.kras.map(computeKra);
      const score = rollup(s.kras.map((k, i) => ({ weight: k.weight, s: kras[i].score })), (x) => x.s);
      const variance = rollup(s.kras.map((k, i) => ({ weight: k.weight, s: kras[i].variance })), (x) => x.s);
      return { score, variance, kras, complete: kras.every((k) => k.complete) };
    });
    const score = rollup(l.subcategories.map((s, i) => ({ weight: s.weight, s: subs[i].score })), (x) => x.s);
    const variance = rollup(l.subcategories.map((s, i) => ({ weight: s.weight, s: subs[i].variance })), (x) => x.s);
    return { advanced: true, score, weighted: score === null ? null : score * Number(l.weight || 0) / 100, variance, subs, complete: subs.every((s) => s.complete) };
  }
  // flat
  let pct = null;
  if (l.kpi_type === "manager_rated") { const r = num(l.rating); if (r !== null) pct = Math.max(0, Math.min(100, r / 10 * 100)); }
  else pct = objPct(l.measurement, l.target, l.actual);
  return { advanced: false, score: pct, weighted: pct === null ? null : pct * Number(l.weight || 0) / 100, complete: pct !== null };
}
const fp = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

/* ---------- small UI atoms ---------- */
function Segmented({ value, onChange, disabled, prefix, accent }) {
  const on = accent === "manager" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-[#0EA5E9] text-white border-[#0EA5E9]";
  const has = value !== null && value !== undefined && value !== "";
  return (
    <div className="flex flex-wrap gap-1 mt-1.5 items-center">
      {Array.from({ length: 11 }, (_, n) => (
        <button key={n} type="button" disabled={disabled} onClick={() => onChange(n)} data-testid={`${prefix}-${n}`}
          className={`w-8 h-8 rounded-md border text-xs font-semibold transition-colors ${has && Number(value) === n ? on : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#0EA5E9]"}`}>{n}</button>
      ))}
      <Input type="number" min="0" max="10" disabled={disabled} value={value ?? ""}
             onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
             className="w-14 h-8 ml-1 text-center text-xs" data-testid={`${prefix}-num`} />
    </div>
  );
}
function VarianceBadge({ variance, direction, testid }) {
  if (variance === null || variance === undefined) return null;
  const a = Math.abs(variance);
  const cls = a <= 1 ? "bg-[#16A34A]/10 text-[#16A34A]" : a < 4 ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#DC2626]/10 text-[#DC2626]";
  const label = a <= 1 ? "aligned" : a < 4 ? "review" : "coach";
  const arrow = direction === "employee_higher" ? "▲" : direction === "manager_higher" ? "▼" : "";
  return <Badge className={`${cls} border-0`} data-testid={testid}>{arrow} Δ{a.toFixed(0)} · {label}</Badge>;
}
function TypeBadge({ k }) {
  const t = kraType(k);
  const cls = t === "Behavioral" ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "bg-[#0EA5E9]/10 text-[#0EA5E9]";
  return <Badge className={`${cls} border-0`}>{t}</Badge>;
}

export default function EvaluationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evalData, setEvalData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/evaluations/${id}`).then(({ data }) => setEvalData(data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const locked = evalData?.status === "finalized" && !["owner", "hr"].includes(user?.role);

  const updateLine = (idx, patch) => setEvalData((prev) => ({ ...prev, lines: prev.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const updateKra = (li, si, ki, patch) => setEvalData((prev) => ({
    ...prev,
    lines: prev.lines.map((l, i) => i !== li ? l : {
      ...l, subcategories: l.subcategories.map((s, j) => j !== si ? s : { ...s, kras: s.kras.map((k, m) => m === ki ? { ...k, ...patch } : k) }),
    }),
  }));

  const computed = useMemo(() => (evalData?.lines || []).map(computeLine), [evalData]);
  const summary = useMemo(() => {
    let total = 0, completed = 0;
    computed.forEach((c) => { if (c.score !== null) total += c.weighted || 0; if (c.complete) completed += 1; });
    return { final: Math.round(total * 100) / 100, completed };
  }, [computed]);

  const coachingSummary = useMemo(() => {
    const gaps = [];
    (evalData?.lines || []).forEach((l) => {
      if (!l.has_subcategories) return;
      l.subcategories.forEach((s) => s.kras.forEach((k) => {
        const c = computeKra(k);
        if (c.variance !== null) gaps.push({ kra: k.statement, kpi: l.name, sub: s.name, employee: num(k.self_rating), manager: num(k.supervisor_rating), variance: c.variance, direction: c.direction });
      }));
    });
    return gaps.sort((a, b) => b.variance - a.variance).slice(0, 5);
  }, [evalData]);

  const save = async (silent = false) => {
    setBusy(true);
    try {
      const payload = {
        lines: evalData.lines,
        strengths: evalData.strengths || "", areas_for_improvement: evalData.areas_for_improvement || "",
        manager_comments: evalData.manager_comments || "", employee_comments: evalData.employee_comments || "",
        coaching_plan: evalData.coaching_plan || "", corrective_actions: evalData.corrective_actions || "",
        follow_up_date: evalData.follow_up_date || null, action_status: evalData.action_status || "not_started",
      };
      const { data } = await api.put(`/evaluations/${id}`, payload);
      setEvalData(data);
      if (!silent) toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try { await save(true); const { data } = await api.post(`/evaluations/${id}/finalize`); setEvalData(data); toast.success("Evaluation finalized"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reopen = async () => { await api.post(`/evaluations/${id}/reopen`); toast.success("Reopened for editing"); load(); };
  const acknowledge = async () => { await api.post(`/evaluations/${id}/acknowledge`, { employee_comments: evalData.employee_comments || "" }); toast.success("Acknowledged"); load(); };
  const downloadPdf = async () => {
    const res = await api.get(`/evaluations/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = `evaluation_${id}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  if (!evalData) return <div className="text-sm text-[#475569]">Loading…</div>;
  const totalLines = evalData.lines.length;
  const progressPct = totalLines === 0 ? 0 : Math.round(summary.completed / totalLines * 100);

  return (
    <div className="space-y-6" data-testid="evaluation-detail-page">
      <button onClick={() => navigate("/evaluations")} className="text-sm text-[#475569] flex items-center gap-1">
        <ArrowLeftIcon size={14} /> Back to evaluations
      </button>

      <Card className={`surface-card border-0 ${scoreBandClass(summary.final)}`}>
        <CardContent className="p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] opacity-80">Evaluation</div>
              <div className="font-display text-2xl sm:text-3xl font-semibold mt-1">{evalData.employee_name}</div>
              <div className="text-sm opacity-90 mt-1">{[evalData.position_name, evalData.department, evalData.branch_name, evalData.team_name].filter(Boolean).join(" · ")}</div>
              <div className="text-sm opacity-90 mt-0.5">{formatDate(evalData.period_start)} → {formatDate(evalData.period_end)}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-5xl font-semibold">{summary.final.toFixed(1)}<span className="text-2xl opacity-80">%</span></div>
              <div className="text-sm opacity-90" data-testid="eval-rating-tier">{scoreBandLabel(summary.final)}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="bg-white/20 text-white border-white/20 capitalize">{evalData.status}</Badge>
            <Badge className="bg-white/20 text-white border-white/20">{summary.completed}/{totalLines} scored</Badge>
            {locked && <Badge className="bg-white/20 text-white border-white/20"><LockKey size={12} className="mr-1" /> Locked</Badge>}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="kpis" className="w-full">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="kpis" data-testid="tab-kpis">KPIs</TabsTrigger>
          <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="coaching" data-testid="tab-coaching">Coaching</TabsTrigger>
        </TabsList>

        <TabsContent value="kpis" className="mt-5">
          <div className="mb-4">
            <Progress value={progressPct} className="h-2" />
            <div className="flex justify-between text-xs text-[#475569] mt-2">
              <span>{summary.completed} of {totalLines} KPIs scored</span><span>{progressPct}%</span>
            </div>
          </div>
          <div className="space-y-3">
            {evalData.lines.map((l, idx) => (
              l.has_subcategories && l.subcategories?.length
                ? <AdvancedKPIRow key={l.kpi_id} idx={idx} line={l} computed={computed[idx]} disabled={locked} onUpdateKra={updateKra} onUpdateLineKraComment={updateKra} />
                : <KPIRow key={l.kpi_id} idx={idx} line={l} computed={computed[idx]} disabled={locked} onChange={(patch) => updateLine(idx, patch)} />
            ))}
          </div>

          {coachingSummary.length > 0 && (
            <CoachingSummaryCard gaps={coachingSummary} />
          )}
        </TabsContent>

        <TabsContent value="comments" className="mt-5 space-y-4">
          <Field label="Strengths" value={evalData.strengths || ""} onChange={(v) => setEvalData({ ...evalData, strengths: v })} disabled={locked} testid="field-strengths" />
          <Field label="Areas for Improvement" value={evalData.areas_for_improvement || ""} onChange={(v) => setEvalData({ ...evalData, areas_for_improvement: v })} disabled={locked} testid="field-improvement" />
          <Field label="Manager Comments" value={evalData.manager_comments || ""} onChange={(v) => setEvalData({ ...evalData, manager_comments: v })} disabled={locked} testid="field-manager-comments" />
          <Field label="Employee Comments" value={evalData.employee_comments || ""} onChange={(v) => setEvalData({ ...evalData, employee_comments: v })} disabled={evalData.status === "finalized" && user?.role !== "employee"} testid="field-employee-comments" />
        </TabsContent>

        <TabsContent value="coaching" className="mt-5 space-y-4">
          <Field label="Coaching Plan" value={evalData.coaching_plan || ""} onChange={(v) => setEvalData({ ...evalData, coaching_plan: v })} disabled={locked} testid="field-coaching-plan" />
          <Field label="Corrective Actions" value={evalData.corrective_actions || ""} onChange={(v) => setEvalData({ ...evalData, corrective_actions: v })} disabled={locked} testid="field-corrective-actions" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="small-label">Follow-up Date</Label>
              <Input type="date" className="mt-2" disabled={locked} value={evalData.follow_up_date || ""}
                     onChange={(e) => setEvalData({ ...evalData, follow_up_date: e.target.value })} data-testid="field-followup-date" />
            </div>
            <div>
              <Label className="small-label">Action Status</Label>
              <select className="mt-2 w-full h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                value={evalData.action_status || "not_started"} onChange={(e) => setEvalData({ ...evalData, action_status: e.target.value })}
                disabled={locked} data-testid="field-action-status">
                <option value="not_started">Not Started</option><option value="in_progress">In Progress</option>
                <option value="completed">Completed</option><option value="escalated">Escalated</option>
              </select>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="sticky-save -mx-4 sm:-mx-6 lg:-mx-10 mt-6 flex-wrap">
        <div className="text-sm text-[#475569] mr-auto">{locked ? "Locked — finalized" : evalData.status === "finalized" ? "Finalized" : "Draft"}</div>
        {evalData.status === "finalized" && (
          <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-button"><FilePdf size={16} className="mr-1" /> Download PDF</Button>
        )}
        {evalData.status === "finalized" && user?.role === "employee" && !evalData.employee_acknowledged && (
          <Button onClick={acknowledge} className="bg-[#16A34A] hover:bg-[#15803D]" data-testid="acknowledge-button"><CheckCircle size={16} className="mr-1" /> Acknowledge</Button>
        )}
        {evalData.status === "finalized" && ["owner", "hr"].includes(user?.role) && (
          <Button variant="outline" onClick={reopen} data-testid="reopen-button">Reopen</Button>
        )}
        {evalData.status !== "finalized" && (
          <>
            <Button variant="outline" onClick={() => save()} disabled={busy} data-testid="save-evaluation-button"><FloppyDisk size={16} className="mr-1" /> Save</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={busy || progressPct < 100} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="finalize-evaluation-button"><CheckCircle size={16} className="mr-1" /> Finalize</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="finalize-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Finalize this evaluation?</AlertDialogTitle>
                  <AlertDialogDescription>Once finalized, the evaluation is locked and an immutable record is added to the audit log. Only Owner or HR can reopen it later.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="finalize-cancel-button">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={finalize} data-testid="finalize-confirm-button" className="bg-[#0EA5E9] hover:bg-[#0284C7]">Yes, finalize</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Advanced KPI (nested) ---------- */
function AdvancedKPIRow({ idx, line, computed, disabled, onUpdateKra }) {
  const lw = Number(line.weight || 0);
  return (
    <Card className="surface-card border-[#BAE6FD]" data-testid={`kpi-row-${idx}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <TreeStructure size={18} weight="duotone" color="#0EA5E9" />
            <div className="font-display text-base font-semibold">{idx + 1}. {line.name}</div>
            <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-0">Advanced</Badge>
            <Badge variant="outline">Weight {lw}%</Badge>
          </div>
          <div className="text-right">
            <div className="font-display text-lg font-semibold" data-testid={`kpi-${idx}-score`}>{fp(computed.score)}</div>
            <div className="text-xs text-[#475569]">KPI score</div>
          </div>
        </div>

        <div className="space-y-3">
          {line.subcategories.map((s, si) => {
            const sc = computed.subs[si];
            const sw = Number(s.weight || 0);
            return (
              <div key={s.subcategory_id} className="rounded-xl border border-[#CBD5E1] bg-[#F8FAFC] p-3" data-testid={`kpi-${idx}-sub-${si}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2"><b className="text-sm">{s.name}</b><Badge variant="outline" className="text-xs">{sw}%</Badge></div>
                  <div className="text-sm"><span className="text-[#475569] text-xs">Subcategory</span> <b className="text-[#0EA5E9] ml-1">{fp(sc.score)}</b></div>
                </div>
                <div className="space-y-2">
                  {s.kras.map((k, ki) => {
                    const kc = sc.kras[ki];
                    const eff = lw * sw * Number(k.weight || 0) / 10000;
                    const isObj = k.primary_source === "objective";
                    return (
                      <div key={k.kra_id} className="rounded-lg border border-[#E2E8F0] bg-white p-3" data-testid={`kpi-${idx}-sub-${si}-kra-${ki}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <b className="text-sm">{k.statement}</b><TypeBadge k={k} />
                            <span className="text-[11px] text-[#0EA5E9] font-medium">Effective {eff.toFixed(1)}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{fp(kc.score)}</span>
                            <VarianceBadge variance={kc.variance} direction={kc.direction} testid={`kpi-${idx}-sub-${si}-kra-${ki}-variance`} />
                          </div>
                        </div>
                        <div className="text-[11px] text-[#94A3B8] mt-1">Official score: {isObj ? "Objective metric" : "Manager rating"} · Employee rating is for variance only</div>

                        {k.enable_self_rating && (
                          <div className="mt-2">
                            <Label className="small-label">Employee Rating ({k.rating_scale})</Label>
                            <Segmented value={k.self_rating} disabled={disabled} prefix={`kpi-${idx}-sub-${si}-kra-${ki}-employee`}
                                       accent="employee" onChange={(v) => onUpdateKra(idx, si, ki, { self_rating: v })} />
                          </div>
                        )}
                        {k.enable_supervisor_rating && (
                          <div className="mt-2">
                            <Label className="small-label">Manager Rating ({k.rating_scale}){!isObj && " · official"}</Label>
                            <Segmented value={k.supervisor_rating} disabled={disabled} prefix={`kpi-${idx}-sub-${si}-kra-${ki}-manager`}
                                       accent="manager" onChange={(v) => onUpdateKra(idx, si, ki, { supervisor_rating: v })} />
                          </div>
                        )}
                        {k.enable_objective && (
                          <div className="mt-2 grid grid-cols-2 gap-3 max-w-xs">
                            <div><Label className="small-label">Target</Label>
                              <Input type="number" className="mt-1.5 h-9" disabled={disabled} value={k.objective_target ?? ""}
                                     onChange={(e) => onUpdateKra(idx, si, ki, { objective_target: e.target.value === "" ? null : Number(e.target.value) })}
                                     data-testid={`kpi-${idx}-sub-${si}-kra-${ki}-target`} /></div>
                            <div><Label className="small-label">Actual</Label>
                              <Input type="number" className="mt-1.5 h-9" disabled={disabled} value={k.objective_actual ?? ""}
                                     onChange={(e) => onUpdateKra(idx, si, ki, { objective_actual: e.target.value === "" ? null : Number(e.target.value) })}
                                     data-testid={`kpi-${idx}-sub-${si}-kra-${ki}-actual`} /></div>
                          </div>
                        )}
                        <Input className="mt-2 h-9" disabled={disabled} value={k.comment || ""} placeholder="Optional notes…"
                               onChange={(e) => onUpdateKra(idx, si, ki, { comment: e.target.value })}
                               data-testid={`kpi-${idx}-sub-${si}-kra-${ki}-comment`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function CoachingSummaryCard({ gaps }) {
  return (
    <Card className="mt-5 border-[#FED7AA] bg-[#FFF7ED]" data-testid="coaching-summary-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[#D97706] font-semibold text-sm mb-3"><Warning size={16} /> Coaching Summary — Top variance gaps (absolute Δ)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-[#94A3B8] text-left">
                <th className="py-1 pr-3">KRA</th><th className="py-1 px-2 text-center">Employee</th><th className="py-1 px-2 text-center">Manager</th>
                <th className="py-1 px-2 text-center">Variance</th><th className="py-1 px-2">Direction</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((g, i) => (
                <tr key={i} className="border-t border-[#FCE3C7]" data-testid={`coaching-gap-${i}`}>
                  <td className="py-2 pr-3"><b>{g.kra}</b><div className="text-[11px] text-[#94A3B8]">{g.kpi} › {g.sub}</div></td>
                  <td className="py-2 px-2 text-center">{g.employee ?? "—"}</td>
                  <td className="py-2 px-2 text-center">{g.manager ?? "—"}</td>
                  <td className="py-2 px-2 text-center"><VarianceBadge variance={g.variance} direction={g.direction} /></td>
                  <td className="py-2 px-2 text-xs">{g.direction === "employee_higher" ? "▲ Employee higher" : g.direction === "manager_higher" ? "▼ Manager higher" : "Aligned"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Flat KPI (legacy, unchanged behavior) ---------- */
function KPIRow({ idx, line, computed, disabled, onChange }) {
  const isMr = line.kpi_type === "manager_rated";
  return (
    <Card className="surface-card border-[#E2E8F0]" data-testid={`kpi-row-${idx}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-base font-semibold">{idx + 1}. {line.name}</div>
              <Badge variant="outline" className="text-xs">{line.category}</Badge>
              <Badge className={isMr ? "bg-[#D97706]/10 text-[#D97706] border-0" : "bg-[#0EA5E9]/10 text-[#0EA5E9] border-0"}>
                {isMr ? "Manager-rated" : "Objective"}
              </Badge>
              <Badge variant="outline">Weight {line.weight}%</Badge>
            </div>
            {line.description && <p className="text-xs text-[#475569] mt-1">{line.description}</p>}
          </div>
          {computed.score != null && (
            <div className="text-right">
              <div className="font-display text-lg font-semibold" data-testid={`kpi-${idx}-score`}>{Number(computed.score).toFixed(1)}%</div>
              <div className="text-xs text-[#475569]">Weighted {Number(computed.weighted || 0).toFixed(2)}</div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {isMr ? (
            <div className="sm:col-span-2">
              <Label className="small-label">Manager Rating (0–10)</Label>
              <Segmented value={line.rating} disabled={disabled} prefix={`kpi-${idx}-rating`}
                         accent="manager" onChange={(v) => onChange({ rating: v })} />
            </div>
          ) : (
            <>
              <div><Label className="small-label">Target</Label>
                <Input type="number" className="mt-2" disabled={disabled} value={line.target ?? ""}
                       onChange={(e) => onChange({ target: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`kpi-${idx}-target`} /></div>
              <div><Label className="small-label">Actual</Label>
                <Input type="number" className="mt-2" disabled={disabled} value={line.actual ?? ""}
                       onChange={(e) => onChange({ actual: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`kpi-${idx}-actual`} /></div>
            </>
          )}
          <div className={isMr ? "" : "sm:col-span-1"}>
            <Label className="small-label">Comment</Label>
            <Input className="mt-2" disabled={disabled} value={line.comment || ""} onChange={(e) => onChange({ comment: e.target.value })}
                   placeholder="Optional notes…" data-testid={`kpi-${idx}-comment`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="small-label">{label}</Label>
      <Textarea className="mt-2" rows={3} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
